import { useState, useEffect, useCallback } from 'react';
import {
  Landmark,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  AlertCircle,
  Loader2,
  AlertTriangle,
  Star,
  CreditCard,
  MapPin,
  Globe,
} from 'lucide-react';
import {
  getInstitutionBanks,
  createInstitutionBank,
  updateInstitutionBank,
  deleteInstitutionBank,
  getInstitutionBankCampuses,
  type BankCampusOption,
} from '@/services/payrollService';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import type {
  InstitutionBankInfo,
  CreateInstitutionBankPayload,
} from '@/types/payroll.types';

// ==================== Form Interface ====================

// Simplified form: agency address/city and SWIFT/IBAN stay in the data model
// and on the cards, but are no longer editable here (kept simple for all roles).
interface BankFormData {
  bankName: string;
  rib: string;
  campusIds: string[];
  accountNumber: string;
  agencyName: string;
}

const emptyBankForm: BankFormData = {
  bankName: '',
  rib: '',
  campusIds: [],
  accountNumber: '',
  agencyName: '',
};

// ==================== Component ====================

export default function InstitutionBankPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Permissions
  const { hasPermission } = usePermissions();
  const canManageBanks = hasPermission(Permission.INSTITUTION_BANK_MANAGE);

  // Data
  const [banks, setBanks] = useState<InstitutionBankInfo[]>([]);
  // all campuses of the tenant (for name display) vs campuses the user may assign
  const [campuses, setCampuses] = useState<BankCampusOption[]>([]);
  const [assignableCampuses, setAssignableCampuses] = useState<BankCampusOption[]>([]);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingBank, setEditingBank] = useState<InstitutionBankInfo | null>(null);
  const [bankForm, setBankForm] = useState<BankFormData>(emptyBankForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Delete confirmation
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    bank: InstitutionBankInfo | null;
  }>({ isOpen: false, bank: null });

  // ==================== Data Loading ====================

  const loadBanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getInstitutionBanks();
      setBanks(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des comptes bancaires');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCampuses = useCallback(async () => {
    try {
      const data = await getInstitutionBankCampuses();
      setCampuses(data.all);
      setAssignableCampuses(data.assignable);
    } catch (err) {
      console.error('Error loading campuses:', err);
    }
  }, []);

  useEffect(() => {
    loadBanks();
    loadCampuses();
  }, [loadBanks, loadCampuses]);

  // Auto-hide success message
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  // Auto-select single campus
  useEffect(() => {
    if (assignableCampuses.length === 1 && showFormModal && !editingBank) {
      setBankForm(prev => ({ ...prev, campusIds: [assignableCampuses[0].id] }));
    }
  }, [assignableCampuses, showFormModal, editingBank]);

  // ==================== Form Validation ====================

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!bankForm.bankName.trim()) {
      errors.bankName = 'Le nom de la banque est requis';
    }

    if (!bankForm.rib.trim()) {
      errors.rib = 'Le RIB est requis';
    } else if (!/^\d{24}$/.test(bankForm.rib.trim())) {
      errors.rib = 'Le RIB doit contenir exactement 24 chiffres';
    }

    if (bankForm.campusIds.length === 0) {
      errors.campusIds = 'Sélectionnez au moins un campus';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ==================== CRUD Operations ====================

  const openAddModal = () => {
    setBankForm(emptyBankForm);
    setEditingBank(null);
    setFormErrors({});
    setShowFormModal(true);
  };

  const openEditModal = (bank: InstitutionBankInfo) => {
    setBankForm({
      bankName: bank.bankName,
      rib: bank.rib,
      campusIds: bank.campusIds || [],
      accountNumber: bank.accountNumber || '',
      agencyName: bank.agencyName || '',
    });
    setEditingBank(bank);
    setFormErrors({});
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingBank(null);
    setBankForm(emptyBankForm);
    setFormErrors({});
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setError(null);
    try {
      const payload: CreateInstitutionBankPayload = {
        bankName: bankForm.bankName.trim(),
        rib: bankForm.rib.trim(),
        campusIds: bankForm.campusIds,
        ...(bankForm.accountNumber.trim() && { accountNumber: bankForm.accountNumber.trim() }),
        ...(bankForm.agencyName.trim() && { agencyName: bankForm.agencyName.trim() }),
      };

      if (editingBank) {
        await updateInstitutionBank(editingBank.id, payload);
        setSuccess('Compte bancaire mis a jour avec succes');
      } else {
        await createInstitutionBank(payload);
        setSuccess('Compte bancaire cree avec succes');
      }

      closeFormModal();
      await loadBanks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.bank) return;

    setSaving(true);
    setError(null);
    try {
      await deleteInstitutionBank(deleteModal.bank.id);
      setSuccess('Compte bancaire supprime avec succes');
      setDeleteModal({ isOpen: false, bank: null });
      await loadBanks();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  // ==================== Render ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Informations Bancaires</h1>
          <p className="text-gray-600 mt-1">
            Gerer les comptes bancaires de l'etablissement
          </p>
        </div>
        {canManageBanks && (
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            Ajouter un compte
          </button>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">Succes</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5 text-green-600" />
          </button>
        </div>
      )}

      {/* Bank Cards */}
      {banks.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {banks.map((bank) => (
            <div
              key={bank.id}
              className="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                {/* Bank Name and Primary Badge */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Landmark className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{bank.bankName}</h3>
                      {bank.agencyName && (
                        <p className="text-sm text-gray-500">{bank.agencyName}</p>
                      )}
                    </div>
                  </div>
                  {bank.isPrimary && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                      <Star className="w-3 h-3" />
                      Principal
                    </span>
                  )}
                </div>

                {/* RIB */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">RIB</span>
                  </div>
                  <p className="font-mono text-sm text-gray-800 bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                    {bank.rib}
                  </p>
                </div>

                {/* Campuses */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Campuses</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {!bank.campusIds || bank.campusIds.length === 0 ? (
                      <span className="inline-flex px-2.5 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-full border border-amber-200">
                        Aucun campus assigné — à compléter
                      </span>
                    ) : (
                      bank.campusIds.map((campusId) => {
                        const campus = campuses.find(c => c.id === campusId);
                        return (
                          <span key={campusId} className="inline-flex px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full border border-gray-200">
                            {campus ? `${campus.name}${campus.isActive ? '' : ' (inactif)'}` : 'Campus supprimé'}
                          </span>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Agency Details */}
                {bank.agencyAddress && (
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-gray-600">
                      {bank.agencyAddress}
                      {bank.agencyCity && `, ${bank.agencyCity}`}
                    </p>
                  </div>
                )}

                {/* SWIFT/IBAN */}
                {(bank.swift || bank.iban) && (
                  <div className="flex items-start gap-2 mb-2">
                    <Globe className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-gray-600">
                      {bank.swift && (
                        <p>
                          <span className="font-medium text-gray-700">SWIFT:</span> {bank.swift}
                        </p>
                      )}
                      {bank.iban && (
                        <p>
                          <span className="font-medium text-gray-700">IBAN:</span> {bank.iban}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {canManageBanks && (
                <div className="border-t border-gray-100 px-5 py-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => openEditModal(bank)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                  <button
                    onClick={() => setDeleteModal({ isOpen: true, bank })}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Supprimer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-12 text-center">
          <Landmark className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-medium text-gray-900 mb-1">Aucun compte bancaire</h3>
          <p className="text-gray-500 mb-4">
            {canManageBanks
              ? "Commencez par ajouter un compte bancaire pour votre etablissement"
              : "Aucun compte bancaire configure pour cet etablissement"}
          </p>
          {canManageBanks && (
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Ajouter un compte
            </button>
          )}
        </div>
      )}

      {/* ==================== Add/Edit Modal ==================== */}
      {showFormModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={closeFormModal} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6 transform transition-all">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingBank ? 'Modifier le compte bancaire' : 'Ajouter un compte bancaire'}
                </h3>
                <button
                  onClick={closeFormModal}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                {/* Bank Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la banque <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankForm.bankName}
                    onChange={(e) => {
                      setBankForm({ ...bankForm, bankName: e.target.value });
                      if (formErrors.bankName) setFormErrors({ ...formErrors, bankName: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      formErrors.bankName ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Ex: Attijariwafa Bank"
                  />
                  {formErrors.bankName && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.bankName}</p>
                  )}
                </div>

                {/* RIB */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    RIB <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={bankForm.rib}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 24);
                      setBankForm({ ...bankForm, rib: value });
                      if (formErrors.rib) setFormErrors({ ...formErrors, rib: '' });
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono ${
                      formErrors.rib ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="24 chiffres"
                    maxLength={24}
                  />
                  <div className="flex items-center justify-between mt-1">
                    {formErrors.rib ? (
                      <p className="text-xs text-red-600">{formErrors.rib}</p>
                    ) : (
                      <span />
                    )}
                    <p className="text-xs text-gray-400">{bankForm.rib.length}/24</p>
                  </div>
                </div>

                {/* Campus Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campus <span className="text-red-500">*</span>{' '}
                    <span className="text-gray-500 text-xs">(sélectionnez au moins un campus)</span>
                  </label>
                  <div className={`space-y-2 p-3 bg-gray-50 rounded-lg border max-h-48 overflow-y-auto ${
                    formErrors.campusIds ? 'border-red-300' : 'border-gray-200'
                  }`}>
                    {assignableCampuses.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-2">Aucun campus disponible</p>
                    ) : (
                      assignableCampuses.map((campus) => (
                        <label key={campus.id} className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-100 rounded">
                          <input
                            type="checkbox"
                            checked={bankForm.campusIds.includes(campus.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setBankForm({ ...bankForm, campusIds: [...bankForm.campusIds, campus.id] });
                              } else {
                                setBankForm({ ...bankForm, campusIds: bankForm.campusIds.filter(id => id !== campus.id) });
                              }
                              if (formErrors.campusIds) setFormErrors({ ...formErrors, campusIds: '' });
                            }}
                            className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="text-sm text-gray-700">{campus.name}</span>
                        </label>
                      ))
                    )}
                  </div>
                  {formErrors.campusIds && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.campusIds}</p>
                  )}
                </div>

                {/* Account Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numero de compte
                  </label>
                  <input
                    type="text"
                    value={bankForm.accountNumber}
                    onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Numero de compte (optionnel)"
                  />
                </div>

                {/* Agency Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de l'agence
                  </label>
                  <input
                    type="text"
                    value={bankForm.agencyName}
                    onChange={(e) => setBankForm({ ...bankForm, agencyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Ex: Agence Maarif"
                  />
                </div>

              </div>

              {/* Modal Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
                <button
                  onClick={closeFormModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingBank ? 'Enregistrer' : 'Creer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== Delete Confirmation Modal ==================== */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={() => setDeleteModal({ isOpen: false, bank: null })}
            />
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 transform transition-all">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full bg-red-100">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Supprimer le compte bancaire
                  </h3>
                  <p className="text-sm text-gray-600">
                    Etes-vous sur de vouloir supprimer ce compte bancaire ?
                  </p>
                  {deleteModal.bank && (
                    <p className="text-sm font-medium text-gray-800 mt-2">
                      {deleteModal.bank.bankName} - {deleteModal.bank.rib}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, bank: null })}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleDelete}
                  disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
